// Phase 5B — Firecrawl sync panel for the Inventory page.
//
// Shows last sync run + a "Sync now" button. Pure presentation: parent
// decides whether the button can fire (via `canSync` / `disabledReason`)
// and server enforces for real.
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { inventorySyncApi } from '../../api';
import { SYNC_STATUS_COLORS } from './constants';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return iso;
  }
}

export default function InventorySyncPanel({ dealerId, canSync, disabledReason, onSynced }) {
  const [lastRun, setLastRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadingRun, setLoadingRun] = useState(false);

  const fetchLast = useCallback(async () => {
    if (!dealerId) { setLastRun(null); return; }
    setLoadingRun(true);
    try {
      const runs = await inventorySyncApi.list({ dealer_id: dealerId, limit: 1 });
      setLastRun(Array.isArray(runs) && runs.length ? runs[0] : null);
    } catch (_) {
      setLastRun(null);
    } finally {
      setLoadingRun(false);
    }
  }, [dealerId]);

  useEffect(() => { fetchLast(); }, [fetchLast]);

  const onSync = async () => {
    setBusy(true);
    try {
      const run = await inventorySyncApi.triggerFirecrawl({ dealerId });
      toast.success(
        `Sync complete — ${run.items_created || 0} created, ` +
        `${run.items_updated || 0} updated, ${run.items_marked_missing || 0} missing.`
      );
      await fetchLast();
      onSynced?.();
    } catch (err) {
      const blockers = err?.data?.detail?.blockers;
      if (Array.isArray(blockers) && blockers.length) {
        toast.error(blockers.join('; '));
      } else {
        toast.error(err.message || 'Sync failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const statusCls = lastRun ? (SYNC_STATUS_COLORS[lastRun.status] || 'bg-gray-100 text-gray-800') : '';
  const errors = lastRun?.errors || [];

  const btn = (
    <Button size="sm" className="gap-2" onClick={onSync} disabled={!canSync || busy}>
      <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
      {busy ? 'Syncing…' : 'Sync now'}
    </Button>
  );

  return (
    <div className="border rounded p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-semibold">Website inventory sync</h2>
          <p className="text-xs text-muted-foreground">
            Crawl the dealer website via Firecrawl and merge changes.
          </p>
        </div>
        {!canSync && disabledReason ? (
          <span title={disabledReason}>{btn}</span>
        ) : (
          btn
        )}
      </div>

      {!canSync && disabledReason && (
        <p className="text-xs text-amber-700">{disabledReason}</p>
      )}

      {loadingRun ? (
        <div className="text-xs text-muted-foreground">Loading last run…</div>
      ) : !lastRun ? (
        <div className="text-xs text-muted-foreground">
          No sync has been run for this dealer yet.
        </div>
      ) : (
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusCls}`}>
              {lastRun.status}
            </span>
            <span className="text-xs text-muted-foreground">
              started {fmtDate(lastRun.started_at)}
              {lastRun.completed_at ? ` · finished ${fmtDate(lastRun.completed_at)}` : ''}
            </span>
            {lastRun.website_url && (
              <a
                className="text-xs underline text-muted-foreground truncate max-w-xs"
                href={lastRun.website_url}
                target="_blank"
                rel="noreferrer"
              >
                {lastRun.website_url}
              </a>
            )}
          </div>

          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
            <div><div className="text-muted-foreground">Pages</div><div className="font-semibold">{lastRun.pages_crawled || 0}</div></div>
            <div><div className="text-muted-foreground">Extracted</div><div className="font-semibold">{lastRun.items_extracted || 0}</div></div>
            <div><div className="text-muted-foreground">Created</div><div className="font-semibold">{lastRun.items_created || 0}</div></div>
            <div><div className="text-muted-foreground">Updated</div><div className="font-semibold">{lastRun.items_updated || 0}</div></div>
            <div><div className="text-muted-foreground">Missing</div><div className="font-semibold">{lastRun.items_marked_missing || 0}</div></div>
            <div><div className="text-muted-foreground">Skipped</div><div className="font-semibold">{lastRun.items_skipped || 0}</div></div>
          </div>

          {errors.length > 0 && (
            <details>
              <summary className="cursor-pointer text-xs text-rose-700">
                {errors.length} error{errors.length === 1 ? '' : 's'}
              </summary>
              <ul className="list-disc pl-5 text-xs text-rose-700 mt-1">
                {errors.slice(0, 10).map((e, i) => (
                  <li key={i}>
                    [{e.stage}] {e.reason}
                    {e.subject ? ` · ${e.subject}` : ''}
                  </li>
                ))}
                {errors.length > 10 && <li>… {errors.length - 10} more</li>}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
